import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { paths } from '@/app/router/paths';
import { BlurredContainer } from '@/features/insights/components/blurred-container';
import { useCurrentOrgId } from '@/features/org/hooks/use-current-org-id';
import { PlanPricing } from '@/features/subscriptions/api/get-pricing';
import { AddonType } from '@/features/subscriptions/data/addons-plans';
import {
  ADDON_SUBSCRIPTIONS_QUERY_KEY,
  useAddonSubscriptions,
} from '@/features/subscriptions/hooks/use-addon-subscriptions';
import { useAssignAddon } from '@/features/subscriptions/hooks/use-assign-addon';
import { useAssignProjectPlan } from '@/features/subscriptions/hooks/use-assign-project-plan';
import {
  PROJECT_SUBSCRIPTION_QUERY_KEY,
  useAvailableProjectSubscriptions,
} from '@/features/subscriptions/hooks/use-available-project-subscriptions';
import { useCreateCheckoutSession } from '@/features/subscriptions/hooks/use-create-checkout-session';
import { useCreatePortalSession } from '@/features/subscriptions/hooks/use-create-portal-session';
import {
  ORG_SUBSCRIPTION_QUERY_KEY,
  useOrgSubscription,
} from '@/features/subscriptions/hooks/use-org-subscription';
import { usePricing } from '@/features/subscriptions/hooks/use-pricing';
import {
  BillingPeriod,
  PlanData,
  PlanType,
} from '@/features/subscriptions/types/subscription-types';
import { getCurrencySymbol } from '@/features/subscriptions/utils/format-price';
import {
  comparePlans,
  getFeatureTranslationKey,
  getIconBgColor,
  getNextUpgradePlan,
  getPlanIcon,
} from '@/features/subscriptions/utils/plan-utils';
import CheckCircle from '@/shared/assets/check-circle.png';
import CheckMarkWhite from '@/shared/assets/checkmark-white.png';
import InfoIcon from '@/shared/assets/info-icon.png';
import { Gradients } from '@/shared/components/theme/gradient';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { useToast } from '@/shared/hooks/use-toast';
import { cn } from '@/shared/utils/cn';
import { useErrorTranslation } from '@/shared/utils/error-handling';

interface PricingCardProps {
  plan: PlanData;
  billingPeriod: BillingPeriod;
  pricing: PlanPricing | undefined;
  isCurrentPlan?: boolean;
  isLoading?: boolean;
  isAnyLoading?: boolean;
  onSelectPlan?: (planType: PlanType) => void;
  onCancelPlan?: () => void;
  onUseExisting?: () => void;
  className?: string;
  manageCurrentPlan?: boolean;
  currentPlanType?: PlanType;
  availableCount?: number;
  isCancelled?: boolean;
  cancelAt?: string | null;
}

export const PricingCard = ({
  plan,
  billingPeriod,
  className,
  isCurrentPlan = false,
  manageCurrentPlan = false,
  projectId,
  onAssignSuccess,
}: {
  plan: PlanData;
  billingPeriod: BillingPeriod;
  className?: string;
  isCurrentPlan?: boolean;
  manageCurrentPlan?: boolean;
  projectId?: number | null;
  onAssignSuccess?: () => void;
}) => {
  const { data: currentOrgPlan } = useOrgSubscription();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const createCheckout = useCreateCheckoutSession();
  const createPortalSession = useCreatePortalSession();
  const organizationId = useCurrentOrgId();
  const { data: pricing } = usePricing();
  const { toast } = useToast();
  const errorTranslation = useErrorTranslation();
  const queryClient = useQueryClient();

  const { data: projectSubscriptions } = useAvailableProjectSubscriptions();
  const { data: availableAddons } = useAddonSubscriptions();
  const assignProjectPlan = useAssignProjectPlan();
  const assignAddon = useAssignAddon();

  const { availableSubscription, availableCount } = useMemo(() => {
    if (!projectId) return { availableSubscription: null, availableCount: 0 };

    if (plan.type === 'project') {
      const subscription = projectSubscriptions?.find(
        (s) => s.plan_type === plan.planType,
      );
      return {
        availableSubscription: subscription ?? null,
        availableCount: subscription ? 1 : 0,
      };
    }

    if (plan.type === 'addon') {
      const addonType = plan.planType as AddonType;
      let count = 0;
      let addon = null;

      if (addonType === 'extra_seat') {
        count = availableAddons?.seats_available ?? 0;
        addon = availableAddons?.addons?.find(
          (a) => a.addon_type === addonType,
        );
      } else if (addonType === 'extra_subproject') {
        count = availableAddons?.subprojects_available ?? 0;
        addon = availableAddons?.addons?.find(
          (a) => a.addon_type === addonType,
        );
      } else if (addonType === 'extra_derivative_kpi') {
        count = availableAddons?.derivative_kpis_available ?? 0;
        addon = availableAddons?.addons?.find(
          (a) => a.addon_type === addonType,
        );
      }

      return { availableSubscription: addon ?? null, availableCount: count };
    }

    return { availableSubscription: null, availableCount: 0 };
  }, [projectId, plan, projectSubscriptions, availableAddons]);

  function handleSelectPlan(planType: PlanType) {
    if (!organizationId) return;

    setLoadingPlanId(planType);
    createCheckout.mutate(
      {
        organizationId,
        planType,
        billingPeriod,
      },
      {
        onSettled: () => {
          setLoadingPlanId(null);
        },
        onError(error) {
          toast({
            title: errorTranslation(error),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [PROJECT_SUBSCRIPTION_QUERY_KEY],
          });
          queryClient.invalidateQueries({
            queryKey: [ADDON_SUBSCRIPTIONS_QUERY_KEY],
          });
          queryClient.invalidateQueries({
            queryKey: [ORG_SUBSCRIPTION_QUERY_KEY],
          });
        },
      },
    );
  }

  function handleCancelPlan() {
    if (!organizationId) return;
    setLoadingPlanId(plan.planType);
    createPortalSession.mutate(
      {
        organizationId,
      },
      {
        onError(error) {
          toast({
            title: errorTranslation(error),
            variant: 'destructive',
          });
        },
        onSettled: () => {
          setLoadingPlanId(null);
        },
      },
    );
  }

  function handleUseExisting() {
    if (!projectId || !availableSubscription) return;

    setIsAssigning(true);

    if (plan.type === 'project') {
      assignProjectPlan.mutate(
        { projectId, subscriptionId: availableSubscription.id },
        {
          onSettled: () => setIsAssigning(false),
          onSuccess: onAssignSuccess,
        },
      );
    } else if (plan.type === 'addon') {
      assignAddon.mutate(
        { projectId, addonId: availableSubscription.id },
        {
          onSettled: () => setIsAssigning(false),
          onSuccess: onAssignSuccess,
        },
      );
    }
  }

  const isCancelled = !!currentOrgPlan?.subscription?.cancel_at;
  const cancelAt = currentOrgPlan?.subscription?.cancel_at ?? null;

  return (
    <PricingCardComponent
      plan={plan}
      billingPeriod={billingPeriod}
      pricing={pricing?.[plan.planType]}
      isLoading={loadingPlanId === plan.id || isAssigning}
      isAnyLoading={loadingPlanId !== null || isAssigning}
      onSelectPlan={handleSelectPlan}
      className={className}
      isCurrentPlan={
        isCurrentPlan ||
        currentOrgPlan?.subscription?.plan_type === plan.planType
      }
      manageCurrentPlan={manageCurrentPlan}
      onCancelPlan={handleCancelPlan}
      currentPlanType={
        currentOrgPlan?.subscription?.plan_type as PlanType | undefined
      }
      availableCount={projectId ? availableCount : undefined}
      onUseExisting={
        projectId && availableCount > 0 ? handleUseExisting : undefined
      }
      isCancelled={isCancelled}
      cancelAt={cancelAt}
    />
  );
};

const PricingCardComponent = ({
  plan,
  billingPeriod,
  pricing,
  isCurrentPlan = false,
  isLoading = false,
  isAnyLoading = false,
  onSelectPlan,
  onCancelPlan,
  onUseExisting,
  className,
  manageCurrentPlan = false,
  currentPlanType,
  availableCount,
  isCancelled = false,
  cancelAt,
}: PricingCardProps) => {
  const { t, i18n } = useTranslation('subscriptions');
  const planName = t(`plans.${plan.planType}.name`);
  const planDescription = t(`plans.${plan.planType}.description`);
  const planButtonLabel = i18n.exists(
    `subscriptions:plans.${plan.planType}.button_label`,
  )
    ? t(`plans.${plan.planType}.button_label` as any)
    : null;

  const planIcon = getPlanIcon(plan.planType);

  const isYearly = billingPeriod === 'yearly';

  const monthlyAmount = pricing?.monthly.amount;
  const yearlyAmount = pricing?.yearly.amount ?? 0;
  const currency = pricing?.monthly.currency ?? 'eur';
  const currencySymbol = getCurrencySymbol(currency);

  const monthlyPrice = isYearly ? Math.round(yearlyAmount / 12) : monthlyAmount;
  const originalMonthlyPrice = monthlyAmount;

  const showDiscount = isYearly && originalMonthlyPrice !== monthlyPrice;
  const nextUpgradePlan = getNextUpgradePlan(plan.planType);

  // Determine if this plan is an upgrade or downgrade from current plan
  const planComparison =
    currentPlanType && !isCurrentPlan
      ? comparePlans(plan.planType, currentPlanType)
      : null;
  const isUpgrade = planComparison === 1;
  const isDowngrade = planComparison === -1;

  function handleSelectPlan(planType: PlanType) {
    onSelectPlan?.(planType);
  }

  function handleCancelPlan() {
    onCancelPlan?.();
  }

  return (
    <BlurredContainer isBlurred={!pricing}>
      <Card
        className={cn(
          'relative flex h-full flex-col border border-none bg-transparent',
          Gradients.transparentWhiteGradient,
          className,
        )}
      >
        <CardHeader className="space-y-6 pb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'flex size-10 items-center justify-center rounded-full border border-white/30 p-2',
                  getIconBgColor(plan.type),
                )}
              >
                {planIcon && (
                  <img
                    src={planIcon}
                    alt={planName}
                    className="size-full object-contain text-white"
                  />
                )}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">{planName}</h3>
              </div>
            </div>
          </div>
          <p className="text-xs text-white/70">{planDescription}</p>
        </CardHeader>

        <CardContent className="flex flex-1 flex-col gap-8">
          <div className="space-y-4">
            <div className="flex items-baseline gap-2">
              {showDiscount && (
                <span className="text-4xl font-light text-custom-grey line-through">
                  {originalMonthlyPrice}
                  {currencySymbol}
                </span>
              )}
              <span className="text-4xl font-light text-white">
                {monthlyPrice}
                {currencySymbol}
              </span>
              <span className="text-sm text-white/70">
                {t('billing.perMonth')}
              </span>
            </div>
            <p className="text-xs">{t(`billing.billed_${billingPeriod}`)}</p>
          </div>
          <div className="flex-1 space-y-3">
            {plan.type === 'organization' &&
              plan.features.map((feature) => (
                <EntitlementEntry
                  key={feature.textKey}
                  text={t(
                    getFeatureTranslationKey(
                      plan.planType,
                      feature.textKey,
                    ) as any,
                  )}
                />
              ))}
            {plan.type === 'project' && (
              <>
                <EntitlementEntry
                  key="seats"
                  text={
                    plan.entitlements.seats === null
                      ? t('includedProject.seat_unlimited')
                      : t('includedProject.seat', {
                          count: plan.entitlements.seats,
                        })
                  }
                />
                <EntitlementEntry
                  key="subprojects"
                  text={
                    plan.entitlements.subprojects === null
                      ? t('includedProject.subproject_unlimited')
                      : t('includedProject.subproject', {
                          count: plan.entitlements.subprojects,
                        })
                  }
                />
                <EntitlementEntry
                  key="standardKpis"
                  text={
                    plan.entitlements.standardKpis === null
                      ? t('includedProject.standardKpi_unlimited')
                      : t('includedProject.standardKpi', {
                          count: plan.entitlements.standardKpis,
                        })
                  }
                />
                <EntitlementEntry
                  key="derivativeKpis"
                  text={
                    plan.entitlements.derivativeKpis === null
                      ? t('includedProject.derivativeKpi_unlimited')
                      : t('includedProject.derivativeKpi', {
                          count: plan.entitlements.derivativeKpis,
                        })
                  }
                />
              </>
            )}
          </div>

          {plan.type === 'organization' && (
            <div className="h-px bg-primary-shade-grey-500/10" />
          )}

          <div className="flex flex-col items-center gap-8">
            {plan.type === 'organization' && (
              <Card className="w-full rounded-sm border-none bg-card-foreground/5 backdrop-blur-sm">
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <img src={InfoIcon} alt="Info" className="size-6" />
                      <p className="text-xs font-semibold text-white">
                        {t('includedProject.title')}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pl-8">
                      {[
                        plan.includedProject.seats === null
                          ? t('includedProject.seat_unlimited')
                          : t('includedProject.seat', {
                              count: plan.includedProject.seats,
                            }),
                        plan.includedProject.subprojects === null
                          ? t('includedProject.subproject_unlimited')
                          : t('includedProject.subproject', {
                              count: plan.includedProject.subprojects,
                            }),
                        plan.includedProject.standardKpis === null
                          ? t('includedProject.standardKpi_unlimited')
                          : t('includedProject.standardKpi', {
                              count: plan.includedProject.standardKpis,
                            }),
                        plan.includedProject.derivativeKpis === null
                          ? t('includedProject.derivativeKpi_unlimited')
                          : t('includedProject.derivativeKpi', {
                              count: plan.includedProject.derivativeKpis,
                            }),
                      ].map((item) => (
                        <div className="flex items-center gap-1.5">
                          <img
                            src={CheckMarkWhite}
                            alt="Check"
                            className="size-4"
                          />
                          <span className="text-2xs text-white/70">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {manageCurrentPlan ? (
              <div className="flex w-full flex-col gap-3">
                {isCancelled && cancelAt && (
                  <p className="text-center text-sm text-orange-400">
                    {t('SubscriptionTab.cancellation_effective', {
                      date: new Date(cancelAt).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }),
                    })}
                  </p>
                )}
                <div className="flex w-full flex-row justify-evenly gap-3">
                  <Button
                    variant="outline"
                    btnType={'secondary'}
                    className="w-full"
                    onClick={handleCancelPlan}
                  >
                    {isLoading && (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    )}
                    {isLoading
                      ? t('buttons.loading')
                      : isCancelled
                        ? t('buttons.manageAllSubscriptions')
                        : t('SubscriptionTab.cancel_plan')}
                  </Button>
                  {!isCancelled && nextUpgradePlan && (
                    <Button
                      variant="greenGradient"
                      btnType={'secondary'}
                      className="w-full"
                      onClick={() => {
                        window.location.href =
                          paths.plansManagement.getHref('org');
                      }}
                    >
                      {t('SubscriptionTab.updgrade_now')}
                    </Button>
                  )}
                </div>
              </div>
            ) : onUseExisting ? (
              <div className="flex w-full flex-col gap-2">
                <Button
                  onClick={onUseExisting}
                  disabled={isAnyLoading}
                  variant="greenGradient"
                  btnType={'secondary'}
                  className="min-w-52"
                >
                  {isLoading && (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  )}
                  {isLoading
                    ? t('buttons.loading')
                    : t('buttons.useExisting', { count: availableCount })}
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => handleSelectPlan(plan.planType)}
                disabled={isCurrentPlan || isAnyLoading}
                variant={isCurrentPlan ? 'outline' : 'greenGradient'}
                btnType={'secondary'}
                className={cn(
                  'min-w-52',
                  isCurrentPlan ? 'bg-white/5' : undefined,
                )}
              >
                {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
                {isLoading
                  ? t('buttons.loading')
                  : isCurrentPlan
                    ? t('buttons.currentPlan')
                    : isUpgrade
                      ? t('buttons.upgrade')
                      : isDowngrade
                        ? t('buttons.downgrade')
                        : (planButtonLabel ?? t('buttons.selectPlan'))}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </BlurredContainer>
  );
};

const EntitlementEntry = ({ key, text }: { key: string; text: string }) => {
  return (
    <div key={key} className="flex items-center gap-3">
      <img src={CheckCircle} alt="Check" className="size-fit w-4" />
      <span className="text-xs">{text}</span>
    </div>
  );
};
